import { NgTemplateOutlet } from '@angular/common'
import { Component, computed, inject, input, OnInit, QueryList, ViewChildren } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { ReactiveFormsModule } from '@angular/forms'
import { EVENT } from '@app/analytics/analytics.cnst.js'
import { addAnalyticsProps, AnalyticsService } from '@app/analytics/analytics.service.js'
import { ICON } from '@app/cnst/icons.cnst.js'
import { BaseModal } from '@app/pages/base.modal.js'
import { DatePipe } from '@app/pipes/date.pipe.js'
import { MarkdownPipe } from '@app/pipes/markdown.pipe.js'
import { TemperaturePipe } from '@app/pipes/temperature.pipe.js'
import { TrPipe } from '@app/pipes/tr.pipe.js'
import { select } from '@app/srv/store.service.js'
import {
  IonButton,
  IonButtons,
  IonFooter,
  IonIcon,
  IonLabel,
  IonRadio,
  IonRadioGroup,
} from '@ionic/angular/standalone'
import { localTime } from '@naturalcycles/js-lib'
import { HARDWARE_WEARABLES } from '@naturalcycles/shared'
import { MultipleEntryItems } from './multiple-entries.service.js'

@Component({
  selector: 'app-multiple-entries-modal',
  templateUrl: './multiple-entries.modal.html',
  styleUrls: ['./multiple-entries.modal.scss'],
  imports: [
    ReactiveFormsModule,
    NgTemplateOutlet,
    DatePipe,
    MarkdownPipe,
    TemperaturePipe,
    TrPipe,
    IonRadioGroup,
    IonRadio,
    IonIcon,
    IonLabel,
    IonFooter,
    IonButtons,
    IonButton,
  ],
})
export class MultipleEntriesModal extends BaseModal implements OnInit {
  className = 'MultipleEntriesModal'

  private analyticsService = inject(AnalyticsService)

  protected title = input<string>()
  protected body = input<string>()
  protected dismissTitle = input<string>('txt-close')
  protected entryItems = input<MultipleEntryItems[]>([])

  @ViewChildren(IonRadioGroup)
  private radios?: QueryList<IonRadioGroup>

  private hwId = toSignal(select(s => s.account.hwId))
  protected readonly wearableMode = computed(() => {
    const hwId = this.hwId()
    return hwId && HARDWARE_WEARABLES.includes(hwId)
  })

  protected readonly ICON = ICON

  public ngOnInit(): void {
    const entryItems = this.entryItems()
    const entries = entryItems.flatMap(item => item.entries)

    addAnalyticsProps({
      temperatures: entries.length,
      days: entryItems.length,
      entries: entries.map(e => {
        const { temperature, date, temperatureUpdatedTimestamp, temperatureMeasuredTimestamp } = e

        return {
          date,
          temperature,
          measured: localTime.orUndefined(temperatureMeasuredTimestamp)?.toPretty(),
          updated: localTime.orUndefined(temperatureUpdatedTimestamp)?.toPretty(),
        }
      }),
    })
  }

  public dismiss(): void {
    const changedTemp = this.radios?.some(r => r.value > 0)
    const clearedTemp = this.radios?.some(r => r.value === undefined)

    const result =
      this.radios?.map((radio, index) => {
        const entry = this.entryItems()[index]?.entries[radio.value]
        return entry
      }) || []

    const entriesToSave = result.filter(Boolean)

    this.analyticsService.trackEvent(EVENT.MULTI_ENTRIES_DISMISS, {
      changedTemp: this.wearableMode() ? undefined : changedTemp,
      clearedTemp: this.wearableMode() ? undefined : clearedTemp,
      entriesToSave: JSON.stringify(entriesToSave),
    })

    void this.dismissModal(entriesToSave)
  }
}
